<template lang="pug">

div

    //- Tabs
    .ui.secondary
        .ui.labels
            router-link.ui.label(:to='{ name: "archive" }' exact)
                | Produkte
            router-link.ui.label(:to='{ name: "archive", query: { type: "jobs" } }' exact)
                | Jobs

    //- Archivierte Produkte
    template(v-if='activeTab === "products"')
        product-table.ui.segment(
            v-if='pageItems.length'
            v-bind:campaign-models='campaigns'
            v-bind:media-production-person-models='mediaProductionPersons'
            v-bind:media-production-user-models='mediaProductionUsers'
            v-bind:product-models='pageItems'
            v-bind:sort-ascending='sortAscending'
            v-bind:variant-models='variantModels'
            v-on:toggle-sort='sortAscending = !sortAscending'
        )
        .ui.bottom.info.icon.message(v-else)
            i.info.circle.icon
            .content: .header Keine archivierten Produkte vorhanden

    //- Archivierte Jobs
    template(v-if='activeTab === "jobs"')
        jobs-table.ui.segment(
            v-if='pageItems.length'
            v-bind:job-models='pageItems'
            v-bind:media-production-person-models='mediaProductionPersons'
            v-bind:media-production-user-models='mediaProductionUsers'
            v-bind:offer-models='offers'
            v-bind:product-models='allProducts'
            v-bind:product-types='productTypes'
            v-bind:sort-ascending='sortAscending'
            v-bind:variant-models='variantModels'
            v-on:toggle-sort='sortAscending = !sortAscending'
        )
        .ui.bottom.info.icon.message(v-else)
            i.info.circle.icon
            .content: .header Keine archivierten Jobs vorhanden

    //- Pagination
    .ui.bottom.secondary(v-if='pageItems.length > 0')
        .ui.two.column.grid
            .middle.aligned.column
                .ui.form
                    .inline.fields(style='margin: 0')
                        label Zeilen pro Seite:
                        .field
                            select.ui.selection.dropdown(ref='pageSizeDropdown')
                                option(v-for='n in pageSizeOptions' v-bind:key='n' v-bind:value='n') {{ n }}
            .right.aligned.middle.aligned.column(v-if='totalPages > 1')
                .ui.pagination.menu
                    a.icon.item(v-bind:class='{ disabled: currentPage === 1 }' v-on:click='currentPage > 1 && (currentPage -= 1)')
                        i.left.chevron.icon
                    a.item(
                        v-for='page in totalPages'
                        v-bind:key='page'
                        v-bind:class='{ active: page === currentPage }'
                        v-on:click='currentPage = page'
                    ) {{ page }}
                    a.icon.item(v-bind:class='{ disabled: currentPage === totalPages }' v-on:click='currentPage < totalPages && (currentPage += 1)')
                        i.right.chevron.icon

</template>

<script>
    import * as R from 'ramda'
    import store from '@/store'
    import ProductTable from '@/components/products/Table'
    import JobsTable from '@/components/jobs/Table.vue'

    // Lädt alle Relationen für ein einzelnes archiviertes Produkt (nur aktuelle Seite)
    async function load_archived_product_relations(product) {
        return Promise.all([
            store.dispatch('variants/loadAllByProductId', { productId: product.id }),
            store.dispatch('users/loadAllByProductId',    { productId: product.id }),
            store.dispatch('persons/loadAllByProductId',  { productId: product.id }),
            store.dispatch('companies/loadOne',            { id: product.company }),
            R.when(R.is(Number), id => store.dispatch('campaigns/loadOne', { id }), product.campaign),
        ])
    }

    // Lädt eine Seite des aktiven Tabs inkl. Relationen
    async function load_page(route, { page = 0, size = 25, sortDirection = 'desc' } = {}) {
        if (route.query.type === 'jobs') {
            const { jobs, totalElements } = await store.dispatch('jobs/loadPage', { page, size, isArchived: true })

            const [ offerModels ] = await Promise.all([
                store.dispatch('offers/loadAllByJobModels',   { jobModels: jobs }),
                store.dispatch('persons/loadAllByJobModels',  { jobModels: jobs }),
                store.dispatch('users/loadAllByJobModels',    { jobModels: jobs }),
            ])

            const variantModels  = await store.dispatch('variants/loadAllByOfferModels',  { offerModels })
            const productModels  = await store.dispatch('products/loadAllByVariantModels', { variantModels })
            await store.dispatch('productTypes/loadAllByProducts', { products: productModels })

            return { items: jobs, totalElements }
        } else {
            const { products, totalElements } = await store.dispatch('products/loadPageArchived', { page, size, sortDirection })
            await Promise.all(products.map(load_archived_product_relations))
            return { items: products, totalElements }
        }
    }

    //noinspection JSUnusedGlobalSymbols
    export default {

        beforeRouteEnter(to, from, next) {
            load_page(to).then(({ items, totalElements }) => next(vm => {
                vm.pageItems     = items
                vm.filteredTotal = totalElements
            }))
        },

        beforeRouteUpdate(to, from, next) {
            this._routeChanging = true
            this.currentPage = 1
            load_page(to, { size: this.itemsPerPage, sortDirection: this.sortAscending ? 'asc' : 'desc' })
                .then(({ items, totalElements }) => {
                    this.pageItems     = items
                    this.filteredTotal = totalElements
                    this._routeChanging = false
                    next()
                })
        },

        components: { ProductTable, JobsTable },

        created() {
            this._routeChanging = false
        },

        mounted() {
            this._initDropdown()
        },

        updated() {
            this._initDropdown()
        },

        data() {
            return {
                currentPage:     1,
                filteredTotal:   0,
                itemsPerPage:    25,
                pageItems:       [],
                pageSizeOptions: [25, 50, 100, 250],
                sortAscending:   false,
            }
        },

        watch: {
            currentPage() {
                if (!this._routeChanging) {
                    this.loadCurrentPage()
                }
            },
            itemsPerPage() {
                if (this.currentPage === 1) {
                    this.loadCurrentPage()
                } else {
                    this.currentPage = 1
                }
            },
            sortAscending() {
                this.loadCurrentPage()
            },
        },

        methods: {
            _initDropdown() {
                const el = this.$refs.pageSizeDropdown
                if (!el || $(el).data('module-dropdown')) return
                $(el).dropdown({
                    onChange: (value) => {
                        const n = parseInt(value, 10)
                        if (n !== this.itemsPerPage) {
                            this.itemsPerPage = n
                            this.currentPage = 1
                        }
                    }
                })
            },

            async loadCurrentPage() {
                const { items, totalElements } = await load_page(this.$route, {
                    page:          this.currentPage - 1,
                    size:          this.itemsPerPage,
                    sortDirection: this.sortAscending ? 'asc' : 'desc',
                })
                this.pageItems     = items
                this.filteredTotal = totalElements
            },
        },

        computed: {

            activeTab() {
                return this.$route.query.type === 'jobs' ? 'jobs' : 'products'
            },

            totalPages() {
                return Math.ceil(this.filteredTotal / this.itemsPerPage)
            },

            campaigns() {
                return R.compose(
                    R.reject(R.isNil),
                    R.map(campaignId => store.getters['campaigns/byId'](campaignId)),
                    R.map(R.prop('campaign'))
                )(this.pageItems)
            },

            mediaProductionPersons() {
                return R.compose(
                    R.reject(R.isNil),
                    R.map(({ person }) => store.getters['persons/byId'](person)),
                    R.reject(R.isNil)
                )(this.mediaProductionUsers)
            },

            mediaProductionUsers() {
                // Jobs haben kein mediaProduction-Feld – das liegt auf den Produkten
                const source = this.activeTab === 'jobs' ? this.allProducts : this.pageItems
                return R.compose(
                    R.reject(R.isNil),
                    R.map(({ mediaProduction }) => store.getters['users/byId'](mediaProduction))
                )(source)
            },

            offers() {
                return store.getters['offers/all']
            },

            allProducts() {
                return store.getters['products/all']
            },

            productTypes() {
                return store.getters['productTypes/all']
            },

            variantModels() {
                return store.getters['variants/all']
            },

        }

    }
</script>
